import { ArrowUpDown, Loader2 } from 'lucide-react';

import { UserPermissionsResponse } from '@/types/service/userManagement';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { ColumnDef } from '@tanstack/react-table';

export type UserManagementRow = UserPermissionsResponse & {
  isUpdating?: boolean;
  updatingPermission?: string;
};

type PermissionKey =
  | 'can_refresh_cache'
  | 'can_manage_configuration'
  | 'can_manage_users'
  | 'can_manage_incidents'
  | 'can_manage_idea_requests'
  | 'can_manage_policies'
  | 'can_use_mcp'
  | 'can_deploy_production'
  | 'can_deploy_nonproduction'
  | 'can_deploy_internal';

const PERMISSION_LABELS: Record<PermissionKey, { label: string; description: string }> = {
  can_refresh_cache: {
    label: 'Refresh Cache',
    description: 'Permission to refresh application cache',
  },
  can_manage_configuration: {
    label: 'Manage Configuration',
    description: 'Permission to manage system configuration',
  },
  can_manage_users: {
    label: 'Manage Users',
    description: 'Permission to manage user accounts and permissions',
  },
  can_manage_incidents: {
    label: 'Manage Service Incidents',
    description: 'Permission to manage service incidents and resolutions',
  },
  can_manage_idea_requests: {
    label: 'Manage Idea Requests',
    description: 'Permission to manage Idea Exchange requests',
  },
  can_manage_policies: {
    label: 'Manage Policies',
    description: 'Permission to manage policies and policy reviews',
  },
  can_use_mcp: {
    label: 'Use MCP',
    description:
      "Lets the user's coding agent run support tasks through the self-service MCP server",
  },
  can_deploy_production: {
    label: 'Initiate Production Environment Actions',
    description: 'Permission to initiate actions on production environments',
  },
  can_deploy_nonproduction: {
    label: 'Initiate Non-Production Environment Actions',
    description: 'Permission to initiate actions on non-production environments',
  },
  can_deploy_internal: {
    label: 'Initiate Internal Environment Actions',
    description: 'Permission to initiate actions on internal environments',
  },
};

export const createUserColumns = (
  onPermissionToggle: (userId: number, permission: string, currentValue: boolean) => Promise<void>,
): ColumnDef<UserManagementRow>[] => [
  {
    accessorKey: 'email',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Email
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      return <div className='font-medium'>{row.getValue('email')}</div>;
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'first_name',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          First Name
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'last_name',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Last Name
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  // Permission columns
  ...Object.entries(PERMISSION_LABELS).map(
    ([key, { label, description }]): ColumnDef<UserManagementRow> => ({
      accessorKey: key,
      header: () => (
        <div className='text-center'>
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <span className='text-xs font-medium cursor-help'>{label}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p className='text-xs'>{description}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      ),
      cell: ({ row }) => {
        const userId = row.original.id;
        const isEnabled = row.getValue(key) as boolean;
        const isUpdating = row.original.isUpdating && row.original.updatingPermission === key;

        return (
          <div className='flex items-center justify-center'>
            {isUpdating ? (
              <Loader2 className='h-4 w-4 animate-spin text-blue-500' />
            ) : (
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <div>
                    <Switch
                      aria-label={`${label} for ${row.original.email}`}
                      checked={isEnabled}
                      onCheckedChange={async () => {
                        await onPermissionToggle(userId, key, isEnabled);
                      }}
                      disabled={isUpdating}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='text-xs'>
                    {isEnabled ? 'Click to disable' : 'Click to enable'} {label.toLowerCase()}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: true,
    }),
  ),
];
